import type { DirectionType, ILayerProps, ResizeOperateType, ScaleOperateType } from '@/version-types';

/** 可参与同一次基础操作吸附仲裁的插件来源。 */
export type SnapSource = 'smart-guides' | 'reference-lines';
/** move 的两个值分别在编辑器逻辑坐标系中独立仲裁。 */
export type MoveSnapSlot = 'move-x' | 'move-y';
/** resize 最终传入 updateLayerResize 的局部尺寸偏移槽位。 */
export type ResizeSnapSlot = 'resize-offset';
/** 当前已启用吸附操作支持的全部输出槽位。 */
export type SnapSlot = MoveSnapSlot | ResizeSnapSlot;

/** 插件向一次基础操作提交的吸附候选。 */
export interface SnapCandidate<TSlot extends SnapSlot = SnapSlot> {
    /** 候选要修正的基础操作输出槽位。 */
    slot: TSlot;
    /** 建议写入基础 update API 的值，单位与对应 slot 的原始逻辑值一致。 */
    value: number;
    /** value 与原始值之差，使用编辑器逻辑坐标单位而非屏幕 CSS 像素。 */
    correction: number;
    /** 候选来源，只用于同距优先级，不用于反向调用插件。 */
    source: SnapSource;
}

/** beforeUpdate 消费方可使用的最小候选提交接口。 */
export interface SnapCandidateCollector<TSlot extends SnapSlot> {
    /** 为指定 slot 提交一个同步吸附候选。 */
    add(candidate: SnapCandidate<TSlot>): void;
}

/** 同距时用户创建的参考线优先，保持显式用户意图。 */
const SNAP_SOURCE_PRIORITY: Record<SnapSource, number> = {
    'smart-guides': 0,
    'reference-lines': 1,
};

/** 收集一次基础操作中的吸附候选，并对每个输出槽位做确定性仲裁。 */
export class SnapCandidateResolver<TSlot extends SnapSlot> implements SnapCandidateCollector<TSlot> {
    /** 当前帧每个 slot 的获胜候选，只在本次同步 beforeUpdate 分发期间有效。 */
    private readonly candidates = new Map<TSlot, SnapCandidate<TSlot>>();

    /**
     * @param rawValues - 每个 slot 未吸附的原始逻辑值；没有候选时原样返回。
     */
    constructor(private readonly rawValues: Readonly<Record<TSlot, number>>) {}

    /** 提交候选；无效数值和不属于本次 resolver 的 slot 会被忽略。 */
    add(candidate: SnapCandidate<TSlot>) {
        if (!(candidate.slot in this.rawValues)) return;
        if (!Number.isFinite(candidate.value) || !Number.isFinite(candidate.correction)) {
            return;
        }

        const current = this.candidates.get(candidate.slot);
        if (!current || this.shouldReplace(current, candidate)) {
            this.candidates.set(candidate.slot, candidate);
        }
    }

    /** 取得 slot 的最终值；没有获胜候选时回退到原始值。 */
    get(slot: TSlot) {
        return this.candidates.get(slot)?.value ?? this.rawValues[slot];
    }

    /** 绝对修正量更小者优先；同距时 reference-lines 优先。 */
    private shouldReplace(current: SnapCandidate<TSlot>, next: SnapCandidate<TSlot>) {
        const currentDistance = Math.abs(current.correction);
        const nextDistance = Math.abs(next.correction);

        if (nextDistance !== currentDistance) {
            return nextDistance < currentDistance;
        }

        return SNAP_SOURCE_PRIORITY[next.source] > SNAP_SOURCE_PRIORITY[current.source];
    }
}

/** 一帧触摸操作的最小公共信息，避免消费插件依赖 ITouchKitEvent 实现。 */
interface OperationFrameEvent {
    /** MouseEvent.x 对应的视口横坐标，单位为 CSS 像素。 */
    pointerX: number;
    /** MouseEvent.y 对应的视口纵坐标，单位为 CSS 像素。 */
    pointerY: number;
    /** 当前帧是否为触摸操作首帧。 */
    isStart: boolean;
    /** 当前帧是否为触摸操作结束帧，不代表异步 history 已完成。 */
    isEnd: boolean;
}

/** 四类基础操作事件共享的稳定上下文。 */
interface BaseOperationEvent extends OperationFrameEvent {
    /** 开始操作时传入基础 update API 的原始图层 ID。 */
    layerId: string;
    /** 操作开始前的只读图层快照，用于几何基准和 history。 */
    oldLayer: ILayerProps;
}

/** 当前实际操作图层；可能因 active pick、sticky 或 agent layout 与原 layerId 不同。 */
interface CurrentOperationLayer {
    /** 每个阶段重新解析的实际操作图层；图层被替换或移除时允许缺失。 */
    layer?: ILayerProps | null;
}

/** move 操作已开始。 */
export interface MoveStartEvent extends BaseOperationEvent, CurrentOperationLayer {}

/** move 写入图层位置前的同步候选收集事件。 */
export interface MoveBeforeUpdateEvent extends BaseOperationEvent, CurrentOperationLayer {
    /** 未吸附的横向累计位移，单位为编辑器逻辑坐标。 */
    rawX: number;
    /** 未吸附的纵向累计位移，单位为编辑器逻辑坐标。 */
    rawY: number;
    /** move-x 与 move-y 的同步候选收集器。 */
    candidates: SnapCandidateCollector<MoveSnapSlot>;
}

/** move 基础更新成功返回后的事件，不由最新 layer 是否存在门控。 */
export interface MoveAfterUpdateEvent extends BaseOperationEvent, CurrentOperationLayer {
    /** 最终传给 updateLayerPosition 的横向累计位移。 */
    appliedX: number;
    /** 最终传给 updateLayerPosition 的纵向累计位移。 */
    appliedY: number;
}

/** move 到达正常同步结束边界；layer 可缺失但事件必须发送。 */
export interface MoveEndEvent extends MoveAfterUpdateEvent {}

/** resize 生命周期共享的手柄方向上下文。 */
interface ResizeOperationEvent extends BaseOperationEvent {
    /** 当前 resize 手柄类型。 */
    operateType: ResizeOperateType;
    /** updateLayerResize 使用的正向或反向尺寸变化系数。 */
    direction: DirectionType;
    /** 是否从左侧或顶部反向调整位置和尺寸。 */
    reverse: boolean;
    /** true 表示本次 resize 改变高度，false 表示改变宽度。 */
    isChangeHeight: boolean;
}

/** resize 操作已开始；首帧保持只初始化、不更新尺寸。 */
export interface ResizeStartEvent extends ResizeOperationEvent, CurrentOperationLayer {}

/** resize 写入尺寸前的同步候选收集事件。 */
export interface ResizeBeforeUpdateEvent extends ResizeOperationEvent, CurrentOperationLayer {
    /** 最终输入槽位未吸附的局部尺寸偏移。 */
    rawOffset: number;
    /** 旋转坐标转换并取整后的横向位移，单位为编辑器逻辑坐标。 */
    rawDisplacementX: number;
    /** 旋转坐标转换并取整后的纵向位移，单位为编辑器逻辑坐标。 */
    rawDisplacementY: number;
    /** resize-offset 的同步候选收集器。 */
    candidates: SnapCandidateCollector<ResizeSnapSlot>;
}

/** resize 基础更新成功返回后的事件。 */
export interface ResizeAfterUpdateEvent extends ResizeOperationEvent, CurrentOperationLayer {
    /** 最终传给 updateLayerResize 的局部尺寸偏移。 */
    appliedOffset: number;
    /** updateLayerResize 返回的最终宽度，单位为编辑器逻辑坐标。 */
    width: number;
    /** updateLayerResize 返回的最终高度，单位为编辑器逻辑坐标。 */
    height: number;
    /** 是否命中基础尺寸下限。 */
    isMin: boolean;
}

/** resize 到达 cursor tip 原同步清理边界；不等待异步 behavior/history。 */
export interface ResizeEndEvent extends ResizeAfterUpdateEvent {}

/** scale 生命周期共享的角点方向上下文。 */
interface ScaleOperationEvent extends BaseOperationEvent {
    /** 当前 scale 角点类型。 */
    operateType: ScaleOperateType;
    /** 横向尺寸变化方向系数。 */
    directionX: DirectionType;
    /** 纵向尺寸变化方向系数。 */
    directionY: DirectionType;
    /** 是否使用反向角点缩放位置。 */
    reverse: boolean;
    /** 是否保持原始宽高比。 */
    keepRatio: boolean;
}

/** scale 操作已开始；首帧保持只初始化、不更新尺寸。 */
export interface ScaleStartEvent extends ScaleOperationEvent, CurrentOperationLayer {}

/** scale 写入尺寸前的同步事件；scale 吸附保持关闭，不提供候选收集器。 */
export interface ScaleBeforeUpdateEvent extends ScaleOperationEvent, CurrentOperationLayer {
    /** 未吸附的横向缩放偏移，单位为编辑器逻辑坐标。 */
    rawOffsetX: number;
    /** 未吸附的纵向缩放偏移，单位为编辑器逻辑坐标。 */
    rawOffsetY: number;
}

/** scale 基础更新成功返回后的事件。 */
export interface ScaleAfterUpdateEvent extends ScaleOperationEvent, CurrentOperationLayer {
    /** 最终传给 updateLayerScale 的横向偏移。 */
    appliedOffsetX: number;
    /** 最终传给 updateLayerScale 的纵向偏移。 */
    appliedOffsetY: number;
    /** updateLayerScale 返回的最终宽度，单位为编辑器逻辑坐标。 */
    width: number;
    /** updateLayerScale 返回的最终高度，单位为编辑器逻辑坐标。 */
    height: number;
    /** 是否命中基础尺寸下限。 */
    isMin: boolean;
}

/** scale 到达 cursor tip 原同步清理边界；scale 吸附仍保持关闭。 */
export interface ScaleEndEvent extends ScaleAfterUpdateEvent {}

/** rotate 生命周期共享的基础上下文。 */
interface RotateOperationEvent extends BaseOperationEvent {}

/** rotate 操作已开始；首帧仍继续执行角度更新。 */
export interface RotateStartEvent extends RotateOperationEvent, CurrentOperationLayer {}

/** rotate 写入角度前的同步事件。 */
export interface RotateBeforeUpdateEvent extends RotateOperationEvent, CurrentOperationLayer {
    /** 基于起点、当前点和中心点计算的未取整角度，单位为度。 */
    originAngle: number;
    /** Math.round 后传给 updateLayerRotate 的原始角度，单位为度。 */
    rawAngle: number;
}

/** rotate 基础更新成功返回后的事件。 */
export interface RotateAfterUpdateEvent extends RotateOperationEvent, CurrentOperationLayer {
    /** updateLayerRotate 内部吸附后的最终角度，单位为度。 */
    rotate: number;
}

/** rotate 到达 cursor tip 原同步清理边界。 */
export interface RotateEndEvent extends RotateAfterUpdateEvent {}

/** MouseOperate 实例事件监听器；事件同步分发，不接受异步结果。 */
export type MouseOperateEventListener<T> = (event: T) => void;
